/*
 * Copyright (c) All right reserved Anvish Inc. 2025
 * This project and any portion thereof may not be reproduced or used in any manner without the express written permission of Anvish Inc.
 */
"use client";
import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Button } from "@heroui/button";
import Image from "next/image";
import "swiper/css";
import { useRouter } from "next/navigation";

interface PromotionSliderProps {
  promoListData: promoListItem[];
}

const PromotionSlider: React.FC<PromotionSliderProps> = ({ promoListData }) => {
  const router = useRouter();
  const sliderRef = useRef<any>(null);
  const [isReachedEnd, setIsReachedEnd] = useState<boolean>(false);
  const [isReachedStart, setIsReachedStart] = useState(false);

  const handlePrev = () => {
    sliderRef.current.swiper.slidePrev();
  };

  const handleNext = () => {
    sliderRef.current.swiper.slideNext();
  };

  // Extreme End Position Checker
  useEffect(() => {
    if (sliderRef.current?.swiper) {
      const swiper = sliderRef.current.swiper;

      setIsReachedStart(swiper.isBeginning);
      setIsReachedEnd(swiper.isEnd);

      // Listen for changes in swiper state
      swiper.on("slideChange", () => {
        setIsReachedStart(swiper.isBeginning);
        setIsReachedEnd(swiper.isEnd);
      });
    }
  }, [sliderRef]);

  return (
    <div className="flex flex-col relative justify-center items-center w-full h-full gap-5 md:gap-10">
      <Swiper
        ref={sliderRef}
        breakpoints={{
          640: {
            slidesPerView: 1,
            spaceBetween: 20,
          },
          768: {
            slidesPerView: 2,
            spaceBetween: 20,
          },
          1024: {
            slidesPerView: 3,
            spaceBetween: 20,
          },
        }}
        className="w-full h-full"
        slidesPerView={1}
        spaceBetween={20}
        wrapperClass={`flex items-center ${promoListData.length < 3 ? "justify-center" : ""}`}
      >
        {promoListData.map((promo, index: number) => (
          <SwiperSlide
            key={index}
            className="!flex w-fit h-full items-center md:justify-center cursor-pointer"
            onClick={() =>
              router.push(
                `/promotions/${promo.product_category}/${promo.meta.slug}`,
              )
            }
          >
            <img
              alt={promo.promotion_banner.title}
              className="aspect-square object-cover object-center"
              height={534}
              src={promo.promotion_banner.meta.download_url}
              width={400}
            />
          </SwiperSlide>
        ))}
      </Swiper>
      <Button
        isIconOnly
        className="hidden md:flex absolute top-1/2 left-2 translate-y-[-50%] z-10 w-14 h-14 bg-white rounded-full  drop-shadow-2xl drop-shadow-black"
        isDisabled={isReachedStart}
        radius="full"
        variant="flat"
        onPress={handlePrev}
      >
        <Image
          alt="Left Arrow"
          className="w-6 h-6 object-contain"
          height={24}
          src="/icons/arrow_left_black.png"
          width={24}
        />
      </Button>
      <Button
        isIconOnly
        className="hidden md:flex absolute top-1/2 right-2 translate-y-[-50%] z-10 w-14 h-14 bg-white rounded-full drop-shadow-2xl drop-shadow-black"
        isDisabled={isReachedEnd}
        radius="full"
        variant="flat"
        onPress={handleNext}
      >
        <Image
          alt="Left Arrow"
          className="w-6 h-6 object-contain rotate-180"
          height={24}
          src="/icons/arrow_left_black.png"
          width={24}
        />
      </Button>
      <div className="flex md:hidden w-full items-center justify-center gap-3 md:gap-4">
        <Button
          isIconOnly
          className="w-10 h-10 bg-black rounded-full"
          isDisabled={isReachedStart}
          radius="full"
          variant="flat"
          onPress={handlePrev}
        >
          <img
            alt="Left Arrow"
            className="w-5 h-5 object-contain rotate-180"
            height={20}
            src="/icons/arrow_left_white.svg"
            width={20}
          />
        </Button>
        <Button
          isIconOnly
          className="w-10 h-10 bg-black rounded-full"
          isDisabled={isReachedEnd}
          radius="full"
          variant="flat"
          onPress={handleNext}
        >
          <img
            alt="Left Arrow"
            className="w-5 h-5 object-contain"
            height={20}
            src="/icons/arrow_left_white.svg"
            width={20}
          />
        </Button>
      </div>
    </div>
  );
};

export default PromotionSlider;
